import axios from "axios";
import { route } from "../router/route";
import { toast } from "react-toastify";

const isProduction = process.env.NODE_ENV === "production";

export const url =
  process.env.REACT_APP_API_URL ||
  (isProduction ? "https://admin.finlive.net" : "http://localhost:8081");

export const fileUrl =
  process.env.REACT_APP_MEDIA_URL ||
  (isProduction
    ? "https://media.finlive.net"
    : "https://media.abbg.co.kr");

const baseURL = `${url}/`;

export const _axios = axios.create({
  baseURL,
  withCredentials: true,
});

let alertFlag = true;

const redirectToLogin = (message = "로그인을 해주세요.") => {
  sessionStorage.clear();

  if (!alertFlag) return;

  toast(message);
  alertFlag = false;

  setTimeout(() => {
    window.location.href = route.login + "?refresh=true";
  }, 1000);
};

_axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const { response, config } = error;
    if (!response) {
      return Promise.reject(error);
    }

    const accessToken = sessionStorage.getItem("token");
    const isExpiredAccessToken =
      response.status === 401 && response.data?.code === 40101;
    const shouldTryRefresh =
      Boolean(accessToken) &&
      !config?._retry &&
      (isExpiredAccessToken || response.status === 403);

    // 액세스 토큰 만료 또는 기존 토큰 무효 시 한 번만 갱신한다.
    if (shouldTryRefresh) {
      config._retry = true;

      try {
        // 인터셉터가 적용되지 않은 axios로 호출해 재귀 갱신을 막는다.
        const resetResponse = await axios.post(
          `${baseURL}api/auth/reset/access`,
          {},
          { withCredentials: true },
        );
        const newToken = resetResponse.data.data.accessToken;

        sessionStorage.setItem("token", newToken);
        config.headers = config.headers ?? {};
        config.headers.authorization = `Bearer ${newToken}`;
        return _axios(config);
      } catch (refreshError) {
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }

    // 인증 실패(갱신 재시도 실패 포함)
    if (response.status === 401) {
      redirectToLogin();
    }
    // 기존 서버의 토큰 만료 응답 호환
    else if (response.status === 406) {
      redirectToLogin("토큰이 만료되었습니다. 다시 로그인을 해주세요.");
    }

    return Promise.reject(error);
  }
);

export default class api {
  static async getAuthorizeHeader() {
    const token = sessionStorage.getItem("token");

    return token
      ? {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        }
      : {};
  }

  static async send({ method, url, data, params, headers }) {
    try {
      const res = await _axios.request({
        url,
        method,
        params,
        data,
        headers: {
          "Access-Control-Allow-Origin": "*",
          ...(await api.getAuthorizeHeader()),
          ...headers,
        },
      });

      return { data: res.data, status: res.status };
    } catch (ex) {
      throw ex;
    }
  }

  static async get(url, params, headers) {
    return await api.send({ method: "GET", url, params, headers });
  }

  static async post(url, data, headers) {
    return await api.send({ method: "POST", url, data, headers });
  }

  static async put(url, data, headers) {
    return await api.send({ method: "PUT", url, data, headers });
  }

  static async patch(url, data, headers) {
    return await api.send({ method: "PATCH", url, data, headers });
  }

  static async del(url, data, headers) {
    return await api.send({ method: "DELETE", url, data, headers });
  }

  static async fileForm(url, file, method, path, params) {
    let formData = new FormData();
    formData.append("file", file);
    formData.append("path", `finlive/dev/${path}`);

    return await api.send({
      url,
      method: method || "PUT",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      data: formData,
      params: params,
    });
  }
}
